import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Button,
  Space,
  Tag,
  Descriptions,
  Alert,
  Form,
  Input,
  Select,
  message,
  Spin,
  Divider,
  Typography,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  FileProtectOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import sslService from '../../services/sslService';

import { userPath } from '../../../shared/config/paths';

const { Step } = Steps;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function CodeSigningOrderDetail() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      updateCurrentStep();
    }
  }, [order]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const response = await sslService.getOrder(orderId);
      if (response.success && response.data) {
        setOrder(response.data);
      } else {
        message.error('Order not found');
        navigate(userPath('/ssl/certificates'));
      }
    } catch (error) {
      console.error('Failed to load order:', error);
      message.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const updateCurrentStep = () => {
    if (!order) return;

    switch (order.status) {
      case 'pending':
        setCurrentStep(0);
        break;
      case 'awaiting_configuration':
        setCurrentStep(1);
        break;
      case 'pending_organization':
      case 'processing':
        setCurrentStep(2);
        break;
      case 'active':
        setCurrentStep(3);
        break;
      case 'failed':
        setCurrentStep(1);
        break;
      default:
        setCurrentStep(0);
    }
  };

  const isEVCodeSigning = () => {
    if (!order) return false;
    const productType = order.product_type || order.type || '';
    return productType.toUpperCase() === 'EV' || productType.toLowerCase().includes('ev ') ||
           ['831', '832', '833'].includes(String(order.provider_product_code || order.product_code || ''));
  };

  const isDocumentSigning = () => {
    if (!order) return false;
    const productType = order.product_type || order.type || '';
    return productType.toLowerCase().includes('document') ||
           ['281', '282', '283'].includes(order.product_code);
  };

  const handleSubmitConfiguration = async (values) => {
    setSubmitting(true);
    try {
      // Paid code-signing orders are configured via /ssl/orders/codesigning (no domain).
      const org = values.org_contact || {};
      const response = await sslService.createCodeSigningOrder({
        order_id: orderId,
        csr: values.csr,
        simplysign_account: values.simplysign_account,
        admin_first_name: values.admin_first_name,
        admin_last_name: values.admin_last_name,
        admin_email: values.admin_email,
        admin_phone: values.admin_phone,
        org_name: org.organization,
        org_country: org.country,
        org_state: org.state,
        org_city: org.city,
        org_address: org.address,
        org_postal: org.postal_code,
        org_phone: org.verification_phone,
        business_category: org.business_category,
        registration_number: org.registration_number,
        jurisdiction_country: org.jurisdiction_country,
        jurisdiction_state: org.jurisdiction_state,
        jurisdiction_city: org.jurisdiction_city,
      });

      if (response.success) {
        message.success(t('Submitted'));
        loadOrder();
      } else {
        message.error(response.error || t('Submission failed'));
      }
    } catch (error) {
      message.error(error?.error || error?.message || t('Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'default', text: 'Pending' },
      awaiting_configuration: { color: 'blue', text: 'Awaiting Configuration' },
      pending_organization: { color: 'orange', text: 'Organization Verification' },
      processing: { color: 'processing', text: 'Processing' },
      active: { color: 'success', text: 'Active' },
      expired: { color: 'default', text: 'Expired' },
      failed: { color: 'error', text: 'Failed' },
      revoked: { color: 'default', text: 'Revoked' }
    };

    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getProductIcon = () => {
    if (isDocumentSigning()) {
      return <FileProtectOutlined style={{ fontSize: 24, color: '#52c41a' }} />;
    }
    return <CodeOutlined style={{ fontSize: 24, color: '#1890ff' }} />;
  };

  const getProductTypeLabel = () => {
    if (isDocumentSigning()) return 'Document Signing';
    if (isEVCodeSigning()) return 'EV Code Signing';
    return 'Standard Code Signing';
  };

  if (loading && !order) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button onClick={() => navigate(userPath('/ssl/certificates'))}>
          &larr; Back to Certificates
        </Button>
      </div>

      <Title level={3}>
        {getProductIcon()} {getProductTypeLabel()} Order Details
      </Title>

      {/* Order Info Card */}
      <Card style={{ marginBottom: 24 }}>
        <Descriptions title="Order Information" bordered column={2}>
          <Descriptions.Item label="Order ID">#{order.id}</Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(order.status)}</Descriptions.Item>
          <Descriptions.Item label="Product">
            <Tag color={isEVCodeSigning() ? 'gold' : 'blue'}>
              {getProductTypeLabel()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Provider">{order.provider_name || 'Certum'}</Descriptions.Item>
          <Descriptions.Item label="Product Name">{order.product_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Created">{order.created_at}</Descriptions.Item>
          {order.expires_at && (
            <Descriptions.Item label="Expires">{order.expires_at}</Descriptions.Item>
          )}
          {order.simplysign_account && (
            <Descriptions.Item label="SimplySign Account">{order.simplysign_account}</Descriptions.Item>
          )}
        </Descriptions>

        {order.status === 'pending' && (
          <Alert type="warning" showIcon style={{ marginTop: 16 }} message={order.payment_error || t('Order awaiting payment')}
            action={order.invoice?.id && (
              <Button type="primary" onClick={() => navigate(userPath(`/billing/invoices/${order.invoice.id}`))}>{t('Pay now')}</Button>
            )} />
        )}
        {order.error_message && order.status !== 'active' && (
          <Alert
            message="Order Failed"
            description={order.error_message}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Workflow Steps */}
      <Card title="Certificate Workflow" style={{ marginBottom: 24 }}>
        <Steps current={currentStep} status={order.status === 'failed' ? 'error' : 'process'}>
          <Step
            title="Order Created"
            description="Order placed and paid"
            icon={currentStep > 0 ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
          />
          <Step
            title="Configuration"
            description="Fill in certificate details"
            icon={currentStep > 1 ? <CheckCircleOutlined /> : currentStep === 1 ? <ClockCircleOutlined /> : null}
          />
          <Step
            title="Organization Verification"
            description="Certum verifies your organization"
            icon={currentStep > 2 ? <CheckCircleOutlined /> : currentStep === 2 ? <ClockCircleOutlined /> : null}
          />
          <Step
            title="Complete"
            description="Certificate issued"
            icon={currentStep === 3 ? <CheckCircleOutlined /> : null}
          />
        </Steps>

        <Divider />

        {/* Step 1: Configuration Form */}
        {currentStep === 1 && (
          <div>
            <Title level={4}>Certificate Configuration</Title>

            <Form form={form} layout="vertical" onFinish={handleSubmitConfiguration}>
              <Divider orientation="left">SimplySign Account</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="SimplySign Account Email"
                    name="simplysign_account"
                    rules={[
                      { required: true, message: 'SimplySign account is required' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                    tooltip="This email will be used to access SimplySign platform for code signing"
                  >
                    <Input placeholder="your-email@company.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">CSR (Certificate Signing Request)</Divider>

              <Form.Item
                label="CSR"
                name="csr"
                rules={[{ required: true, message: 'CSR is required' }]}
                tooltip="Generate CSR with your organization's information"
              >
                <TextArea
                  rows={6}
                  placeholder="-----BEGIN CERTIFICATE REQUEST-----
...
-----END CERTIFICATE REQUEST-----"
                />
              </Form.Item>

              <Alert
                message="CSR Generation"
                description={
                  <div>
                    <p>Generate CSR using OpenSSL:</p>
                    <code style={{ display: 'block', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                      openssl req -new -newkey rsa:2048 -nodes -keyout private.key -out request.csr
                    </code>
                    <p style={{ marginTop: 8, marginBottom: 0 }}>
                      Include your organization name in the CSR&apos;s O (Organization) field.
                    </p>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Divider orientation="left">Organization Information</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Organization Name"
                    name={['org_contact', 'organization']}
                    rules={[{ required: true, message: 'Organization name is required' }]}
                  >
                    <Input placeholder="Your Company Ltd." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Country"
                    name={['org_contact', 'country']}
                    rules={[{ required: true, message: 'Country is required' }]}
                  >
                    <Select placeholder="Select country">
                      <Option value="CN"><CountryFlag code="CN" />China (CN)</Option>
                      <Option value="US"><CountryFlag code="US" />United States (US)</Option>
                      <Option value="GB"><CountryFlag code="GB" />United Kingdom (GB)</Option>
                      <Option value="DE"><CountryFlag code="DE" />Germany (DE)</Option>
                      <Option value="JP"><CountryFlag code="JP" />Japan (JP)</Option>
                      <Option value="SG"><CountryFlag code="SG" />Singapore (SG)</Option>
                      <Option value="HK"><CountryFlag code="HK" />Hong Kong (HK)</Option>
                      <Option value="TW"><CountryFlag code="TW" />Taiwan (TW)</Option>
                      <Option value="KR"><CountryFlag code="KR" />South Korea (KR)</Option>
                      <Option value="FR"><CountryFlag code="FR" />France (FR)</Option>
                      <Option value="CA"><CountryFlag code="CA" />Canada (CA)</Option>
                      <Option value="AU"><CountryFlag code="AU" />Australia (AU)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="State/Province"
                    name={['org_contact', 'state']}
                    rules={[{ required: true, message: 'State/Province is required' }]}
                  >
                    <Input placeholder="e.g., Beijing, California" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City/Locality"
                    name={['org_contact', 'city']}
                    rules={[{ required: true, message: 'City is required' }]}
                  >
                    <Input placeholder="e.g., Beijing, San Francisco" />
                  </Form.Item>
                </Col>
              </Row>

              {/* EV Code Signing Additional Fields */}
              {isEVCodeSigning() && (
                <>
                  <Divider orientation="left">EV Certificate Additional Information</Divider>

                  <Alert
                    message="EV Code Signing Requirements"
                    description="EV certificates provide the highest level of trust. Microsoft SmartScreen will immediately trust your software without requiring download reputation."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Business Category"
                        name={['org_contact', 'business_category']}
                        rules={[{ required: true, message: 'Business category is required' }]}
                      >
                        <Select placeholder="Select business category">
                          <Option value="Private Organization">Private Organization</Option>
                          <Option value="Government Entity">Government Entity</Option>
                          <Option value="Business Entity">Business Entity</Option>
                          <Option value="Non-Commercial Entity">Non-Commercial Entity</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Registration Number"
                        name={['org_contact', 'registration_number']}
                        rules={[{ required: true, message: 'Registration number is required' }]}
                        tooltip="Company registration number or unified social credit code"
                      >
                        <Input placeholder="e.g., 91110000MA001XXX" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Jurisdiction Country"
                        name={['org_contact', 'jurisdiction_country']}
                        rules={[{ required: true, message: 'Jurisdiction country is required' }]}
                      >
                        <Select placeholder="Select country">
                          <Option value="CN"><CountryFlag code="CN" />China (CN)</Option>
                          <Option value="US"><CountryFlag code="US" />United States (US)</Option>
                          <Option value="GB"><CountryFlag code="GB" />United Kingdom (GB)</Option>
                          <Option value="DE"><CountryFlag code="DE" />Germany (DE)</Option>
                          <Option value="JP"><CountryFlag code="JP" />Japan (JP)</Option>
                          <Option value="SG"><CountryFlag code="SG" />Singapore (SG)</Option>
                          <Option value="HK"><CountryFlag code="HK" />Hong Kong (HK)</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Jurisdiction State"
                        name={['org_contact', 'jurisdiction_state']}
                      >
                        <Input placeholder="Optional" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Jurisdiction City"
                        name={['org_contact', 'jurisdiction_city']}
                      >
                        <Input placeholder="Optional" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Street Address"
                        name={['org_contact', 'address']}
                        rules={[{ required: true, message: 'Address is required' }]}
                      >
                        <Input placeholder="123 Main Street" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Postal Code"
                        name={['org_contact', 'postal_code']}
                      >
                        <Input placeholder="100000" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Tax ID"
                        name={['org_contact', 'tax_id']}
                        tooltip="Tax identification number (optional for some countries)"
                      >
                        <Input placeholder="Optional" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Verification Phone"
                        name={['org_contact', 'verification_phone']}
                        tooltip="Phone number that can be verified through public records"
                      >
                        <Input placeholder="+86-10-12345678" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              <Divider orientation="left">Admin Contact</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="First Name"
                    name="admin_first_name"
                    rules={[{ required: true, message: 'First name is required' }]}
                  >
                    <Input placeholder="John" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Last Name"
                    name="admin_last_name"
                    rules={[{ required: true, message: 'Last name is required' }]}
                  >
                    <Input placeholder="Doe" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Email"
                    name="admin_email"
                    rules={[
                      { required: true, message: 'Email is required' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input placeholder="admin@company.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Phone"
                    name="admin_phone"
                    rules={[{ required: true, message: 'Phone is required' }]}
                  >
                    <Input placeholder="+86-138-xxxx-xxxx" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting} size="large">
                  Submit Configuration
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        {/* Step 2: Organization Verification */}
        {currentStep === 2 && (
          <div>
            <Alert
              message="Organization Verification in Progress"
              description={
                <div>
                  <p>Certum is verifying your organization information. This process typically takes:</p>
                  <ul>
                    <li><strong>Standard Code Signing (OV):</strong> 1-3 business days</li>
                    <li><strong>EV Code Signing:</strong> 3-5 business days</li>
                  </ul>
                  <p style={{ marginBottom: 0 }}>
                    You may receive a verification call or email from Certum. Please ensure your contact information is accurate and accessible.
                  </p>
                </div>
              }
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
            />
          </div>
        )}

        {/* Step 3: Complete */}
        {currentStep === 3 && (
          <div>
            <Alert
              message="Certificate Issued"
              description={
                <div>
                  <p>Your code signing certificate has been issued and is ready for use!</p>
                  <p style={{ marginBottom: 0 }}>
                    Access your certificate through the SimplySign platform using your registered email.
                  </p>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space>
              <Button
                type="primary"
                href="https://simplysign.certum.eu/"
                target="_blank"
              >
                Open SimplySign Platform
              </Button>
            </Space>

            {order.simplysign_account && (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>Certificate Details</Title>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="SimplySign Account">
                    {order.simplysign_account}
                  </Descriptions.Item>
                  <Descriptions.Item label="Certificate Type">
                    {getProductTypeLabel()}
                  </Descriptions.Item>
                  {order.expires_at && (
                    <Descriptions.Item label="Valid Until">
                      {order.expires_at}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
